import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  Search,
  Mail,
  Phone,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Shield,
  Zap,
  Info,
  CreditCard,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvestigatePage = () => {
  const { getAuthHeader } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [usage, setUsage] = useState(null);
  const [credits, setCredits] = useState({ email: 0, phone: 0, social: 0 });
  const [emailQuery, setEmailQuery] = useState("");
  const [phoneQuery, setPhoneQuery] = useState("");
  const [emailResult, setEmailResult] = useState(null);
  const [phoneResult, setPhoneResult] = useState(null);
  const [socialResult, setSocialResult] = useState(null);
  const [loading, setLoading] = useState({ email: false, phone: false, social: false });
  const [buyingReport, setBuyingReport] = useState(false);

  useEffect(() => {
    fetchUsage();
    fetchCredits();
    
    // Handle single report purchase success
    const singleReportStatus = searchParams.get('single_report');
    const sessionId = searchParams.get('session_id');
    if (singleReportStatus === 'success' && sessionId) {
      checkSingleReportStatus(sessionId);
      // Clean up URL params
      searchParams.delete('single_report');
      searchParams.delete('session_id');
      setSearchParams(searchParams);
    }
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/user/usage`, {
        headers: getAuthHeader()
      });
      setUsage(response.data);
    } catch (error) {
      console.error("Failed to fetch usage");
    }
  };

  const fetchCredits = async () => {
    try {
      const response = await axios.get(`${API}/user/credits`, {
        headers: getAuthHeader()
      });
      setCredits(response.data);
    } catch (error) {
      console.error("Failed to fetch credits");
    }
  };

  const checkSingleReportStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/subscription/single-report/status/${sessionId}`, {
        headers: getAuthHeader()
      });
      if (response.data.payment_status === 'paid') {
        toast.success("Single report purchased! You now have 1 lookup credit for each investigation type.");
        fetchCredits();
        fetchUsage();
      }
    } catch (error) {
      console.error("Failed to check single report status");
    }
  };

  const buySingleReport = async () => {
    setBuyingReport(true);
    try {
      const response = await axios.post(
        `${API}/subscription/single-report`,
        null,
        {
          headers: getAuthHeader(),
          params: { origin_url: window.location.origin }
        }
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create checkout");
      setBuyingReport(false);
    }
  };

  const investigateEmail = async (e) => {
    e.preventDefault();
    if (!emailQuery) return;
    
    setLoading(prev => ({ ...prev, email: true }));
    setEmailResult(null);
    
    try {
      const response = await axios.get(`${API}/investigate/email/${encodeURIComponent(emailQuery)}`, {
        headers: getAuthHeader()
      });
      setEmailResult(response.data);
      fetchUsage();
      fetchCredits();
      toast.success("Email investigation complete");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Investigation failed");
    } finally {
      setLoading(prev => ({ ...prev, email: false }));
    }
  };

  const investigatePhone = async (e) => {
    e.preventDefault();
    if (!phoneQuery) return;
    
    setLoading(prev => ({ ...prev, phone: true }));
    setPhoneResult(null);
    
    try {
      const response = await axios.get(`${API}/investigate/phone/${encodeURIComponent(phoneQuery)}`, {
        headers: getAuthHeader()
      });
      setPhoneResult(response.data);
      fetchUsage();
      fetchCredits();
      toast.success("Phone investigation complete");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Investigation failed");
    } finally {
      setLoading(prev => ({ ...prev, phone: false }));
    }
  };

  const investigateSocial = async () => {
    if (!emailQuery && !phoneQuery) {
      toast.error("Enter an email or phone number first");
      return;
    }
    
    setLoading(prev => ({ ...prev, social: true }));
    setSocialResult(null);
    
    try {
      const params = new URLSearchParams();
      if (emailQuery) params.append("email", emailQuery);
      if (phoneQuery) params.append("phone", phoneQuery);
      
      const response = await axios.get(`${API}/investigate/social?${params.toString()}`, {
        headers: getAuthHeader()
      });
      setSocialResult(response.data);
      fetchUsage();
      fetchCredits();
      toast.success("Social investigation complete");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Investigation failed");
    } finally {
      setLoading(prev => ({ ...prev, social: false }));
    }
  };

  const getFraudColor = (score) => {
    if (score >= 70) return "text-red-500";
    if (score >= 40) return "text-amber-500";
    return "text-emerald-500";
  };

  const getFraudBg = (score) => {
    if (score >= 70) return "bg-red-500";
    if (score >= 40) return "bg-amber-500";
    return "bg-emerald-500";
  };

  return (
    <div className="space-y-6" data-testid="investigate-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
          <Search className="w-7 h-7 text-primary" />
          Scam Investigation
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Investigate emails, phones, and social profiles for fraud indicators
        </p>
      </div>

      {/* Usage Stats */}
      {usage && (
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Monthly Usage ({usage.tier} tier)</span>
              </div>
              <Badge variant="outline">{usage.tier}</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Email</span>
                  <span>{usage.usage.email.used}/{usage.usage.email.limit}</span>
                </div>
                <Progress value={(usage.usage.email.used / Math.max(usage.usage.email.limit, 1)) * 100} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Phone</span>
                  <span>{usage.usage.phone.used}/{usage.usage.phone.limit}</span>
                </div>
                <Progress value={(usage.usage.phone.used / Math.max(usage.usage.phone.limit, 1)) * 100} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Social</span>
                  <span>{usage.usage.social.used}/{usage.usage.social.limit}</span>
                </div>
                <Progress value={(usage.usage.social.used / Math.max(usage.usage.social.limit, 1)) * 100} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>IP</span>
                  <span>{usage.usage.ip.used}/{usage.usage.ip.limit}</span>
                </div>
                <Progress value={(usage.usage.ip.used / Math.max(usage.usage.ip.limit, 1)) * 100} className="h-2" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Single Report Credits & Buy Option */}
      <Card className="bg-gradient-to-r from-primary/10 to-emerald-500/10 border-primary/30">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm font-medium">Single Report Credits</p>
                <p className="text-xs text-muted-foreground">
                  Buy one lookup at a time when you're out of monthly credits
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <Mail className="w-3 h-3 text-muted-foreground" />
                  <span className={credits.email > 0 ? "text-emerald-500 font-medium" : "text-muted-foreground"}>
                    {credits.email}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Phone className="w-3 h-3 text-muted-foreground" />
                  <span className={credits.phone > 0 ? "text-emerald-500 font-medium" : "text-muted-foreground"}>
                    {credits.phone}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3 text-muted-foreground" />
                  <span className={credits.social > 0 ? "text-emerald-500 font-medium" : "text-muted-foreground"}>
                    {credits.social}
                  </span>
                </div>
              </div>
              <Button 
                onClick={buySingleReport} 
                disabled={buyingReport}
                size="sm"
                className="gap-2"
                data-testid="buy-single-report-btn"
              >
                <CreditCard className="w-4 h-4" />
                {buyingReport ? "Processing..." : "Buy Report - $2.99"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Investigation Tabs */}
      <Tabs defaultValue="email" className="space-y-4">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="email" className="gap-2" data-testid="email-tab">
            <Mail className="w-4 h-4" />
            Email
          </TabsTrigger>
          <TabsTrigger value="phone" className="gap-2" data-testid="phone-tab">
            <Phone className="w-4 h-4" />
            Phone
          </TabsTrigger>
          <TabsTrigger value="social" className="gap-2" data-testid="social-tab">
            <Users className="w-4 h-4" />
            Social
          </TabsTrigger>
        </TabsList>

        {/* Email Tab */}
        <TabsContent value="email">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  Email Investigation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={investigateEmail} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      placeholder="scammer@example.com"
                      value={emailQuery}
                      onChange={(e) => setEmailQuery(e.target.value)}
                      className="bg-background font-mono"
                      data-testid="email-input"
                    />
                  </div>
                  <Button type="submit" disabled={loading.email} className="w-full" data-testid="investigate-email-btn">
                    {loading.email ? "Investigating..." : "Investigate Email"}
                  </Button>
                </form>
                
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>• Checks data breaches & leaked credentials</p>
                  <p>• Identifies disposable/temporary emails</p>
                  <p>• Finds linked social profiles</p>
                  <p>• Calculates fraud risk score</p>
                </div>
              </CardContent>
            </Card>

            {emailResult && !emailResult.error && (
              <Card className="bg-card/50 border-border" data-testid="email-result">
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    <span>Results: {emailResult.email}</span>
                    <Badge className={`${getFraudBg(emailResult.fraud_score)} text-white`}>
                      Risk: {emailResult.fraud_score}/100
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      {emailResult.suspicious ? <XCircle className="w-4 h-4 text-red-500" /> : <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      <span className="text-sm">Suspicious: {emailResult.suspicious ? "Yes" : "No"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {emailResult.blacklisted ? <XCircle className="w-4 h-4 text-red-500" /> : <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      <span className="text-sm">Blacklisted: {emailResult.blacklisted ? "Yes" : "No"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {emailResult.data_breach ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      <span className="text-sm">Data Breach: {emailResult.data_breach ? "Yes" : "No"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {emailResult.credentials_leaked ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      <span className="text-sm">Credentials Leaked: {emailResult.credentials_leaked ? "Yes" : "No"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {emailResult.disposable ? <XCircle className="w-4 h-4 text-red-500" /> : <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      <span className="text-sm">Disposable: {emailResult.disposable ? "Yes" : "No"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {emailResult.spoofable ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      <span className="text-sm">Spoofable: {emailResult.spoofable ? "Yes" : "No"}</span>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <p className="text-sm"><strong>Reputation:</strong> {emailResult.reputation}</p>
                    <p className="text-sm"><strong>References:</strong> {emailResult.references} online mentions</p>
                    {emailResult.first_seen && <p className="text-sm"><strong>First Seen:</strong> {emailResult.first_seen}</p>}
                    {emailResult.profiles && emailResult.profiles.length > 0 && (
                      <div>
                        <p className="text-sm font-medium">Social Profiles Found:</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {emailResult.profiles.map((p, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{p}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Phone Tab */}
        <TabsContent value="phone">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary" />
                  Phone Investigation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={investigatePhone} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={phoneQuery}
                      onChange={(e) => setPhoneQuery(e.target.value)}
                      className="bg-background font-mono"
                      data-testid="phone-input"
                    />
                  </div>
                  <Button type="submit" disabled={loading.phone} className="w-full" data-testid="investigate-phone-btn">
                    {loading.phone ? "Investigating..." : "Investigate Phone"}
                  </Button>
                </form>
                
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>• Identifies carrier and line type</p>
                  <p>• Detects VoIP/burner phones (scam indicator)</p>
                  <p>• Shows registered location</p>
                  <p>• Validates phone number format</p>
                </div>
              </CardContent>
            </Card>

            {phoneResult && !phoneResult.error && (
              <Card className="bg-card/50 border-border" data-testid="phone-result">
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    <span>Results: {phoneResult.phone_number}</span>
                    <Badge className={`${getFraudBg(phoneResult.fraud_score)} text-white`}>
                      Risk: {phoneResult.fraud_score}/100
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      {phoneResult.valid ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                      <span className="text-sm">Valid: {phoneResult.valid ? "Yes" : "No"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {phoneResult.is_voip ? <AlertTriangle className="w-4 h-4 text-red-500" /> : <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      <span className="text-sm">VoIP/Burner: {phoneResult.is_voip ? "Yes" : "No"}</span>
                    </div>
                  </div>
                  
                  {phoneResult.is_voip && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded">
                      <div className="flex items-center gap-2 text-red-500">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm font-medium">Warning: VoIP Number Detected</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        VoIP numbers are commonly used by scammers as they are cheap, disposable, and hard to trace.
                      </p>
                    </div>
                  )}
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <p className="text-sm"><strong>Line Type:</strong> {phoneResult.line_type || "Unknown"}</p>
                    <p className="text-sm"><strong>Carrier:</strong> {phoneResult.carrier || "Unknown"}</p>
                    <p className="text-sm"><strong>Country:</strong> {phoneResult.country_name || "Unknown"}</p>
                    {phoneResult.state && <p className="text-sm"><strong>State:</strong> {phoneResult.state} ({phoneResult.state_abbr})</p>}
                    {phoneResult.city && <p className="text-sm"><strong>City:</strong> {phoneResult.city}</p>}
                    {phoneResult.location && <p className="text-sm"><strong>Full Location:</strong> {phoneResult.location}</p>}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Social Tab */}
        <TabsContent value="social">
          <Card className="bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                Social Profile Investigation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter an email or phone in the other tabs first, then click below to find linked social profiles.
              </p>
              
              <div className="flex gap-2">
                {emailQuery && <Badge variant="outline">Email: {emailQuery}</Badge>}
                {phoneQuery && <Badge variant="outline">Phone: {phoneQuery}</Badge>}
              </div>
              
              <Button 
                onClick={investigateSocial} 
                disabled={loading.social || (!emailQuery && !phoneQuery)} 
                className="w-full"
                data-testid="investigate-social-btn"
              >
                {loading.social ? "Searching..." : "Find Social Profiles"}
              </Button>
              
              {socialResult && (
                <div className="p-4 bg-background rounded border border-border space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Social Presence</span>
                    <Badge className={socialResult.has_social_presence ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-500/10 text-amber-500"}>
                      {socialResult.has_social_presence ? "Found" : "Limited/None"}
                    </Badge>
                  </div>
                  
                  {socialResult.profiles_found && socialResult.profiles_found.length > 0 ? (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Profiles Found:</p>
                      <div className="flex flex-wrap gap-2">
                        {socialResult.profiles_found.map((p, i) => (
                          <Badge key={i} variant="outline">{p.platform}</Badge>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No social profiles found - this could indicate a fake identity.</p>
                  )}
                  
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-sm">Fraud Risk Score</span>
                    <span className={`font-mono font-bold ${getFraudColor(socialResult.fraud_score)}`}>
                      {socialResult.fraud_score}/100
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pricing Info */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-medium">Upgrade for More Lookups</p>
              <div className="grid grid-cols-3 gap-4 mt-3 text-xs">
                <div>
                  <p className="font-medium">Basic - $9.99/mo</p>
                  <p className="text-muted-foreground">20 phone, 20 social, 20 email</p>
                </div>
                <div>
                  <p className="font-medium">Advanced - $24.99/mo</p>
                  <p className="text-muted-foreground">100 phone, 100 social, 100 email</p>
                </div>
                <div>
                  <p className="font-medium">Professional - $49.99/mo</p>
                  <p className="text-muted-foreground">500 phone, 500 social, 500 email</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvestigatePage;
